import { getMessaging, getToken, isSupported, onMessage, type MessagePayload } from 'firebase/messaging';
import { firebaseApp, hasFirebaseConfig } from './app';
import { supabase } from '@/services/supabase/client';

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

async function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) {
    return existing;
  }

  return navigator.serviceWorker.register('/sw.js');
}

export async function isMessagingSupported(): Promise<boolean> {
  if (!hasFirebaseConfig) {
    return false;
  }

  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission !== 'default') {
    return Notification.permission;
  }

  return Notification.requestPermission();
}

async function registerPushToken(token: string) {
  try {
    // Import device fingerprinting utilities
    const {
      generateDeviceFingerprint,
      getDeviceName,
      getBrowserName,
      getBrowserVersion,
      getOSName,
      getOSVersion,
    } = await import('./deviceFingerprint');

    // Generate stable device ID
    const deviceId = await generateDeviceFingerprint();
    const deviceName = getDeviceName();
    const browserName = getBrowserName();
    const browserVersion = getBrowserVersion();
    const osName = getOSName();
    const osVersion = getOSVersion();

    console.log('[Push] Registering token with device fingerprint:', {
      deviceId: deviceId.slice(0, 8) + '...',
      deviceName,
      browser: `${browserName} ${browserVersion}`,
      os: `${osName} ${osVersion}`,
    });

    // Type assertion needed until types are regenerated after migration
    const { error } = await (supabase.rpc as any)('register_push_token', {
      p_push_token: token,
      p_device_type: 'web',
      p_device_name: deviceName,
      p_device_id: deviceId,
      p_user_agent: navigator.userAgent,
      p_browser_name: browserName,
      p_browser_version: browserVersion,
      p_os_name: osName,
      p_os_version: osVersion,
    });

    if (error) {
      console.error('[Push] Failed to save push token:', error);
    } else {
      console.log('[Push] Successfully registered token with fingerprint');
    }
  } catch (error) {
    console.error('[Push] Failed to register push token:', error);
  }
}

export async function registerForPushNotifications(): Promise<string | null> {
  console.log('[Push] Starting push notification registration...');

  if (!hasFirebaseConfig || !vapidKey || !firebaseApp) {
    console.warn('[Push] Missing Firebase configuration or VAPID key', {
      hasFirebaseConfig,
      hasVapidKey: !!vapidKey,
      hasFirebaseApp: !!firebaseApp,
    });
    return null;
  }

  const supported = await isMessagingSupported();
  if (!supported) {
    console.warn('[Push] Firebase Messaging is not supported in this browser');
    return null;
  }

  console.log('[Push] Requesting notification permission...');
  const permission = await requestNotificationPermission();
  console.log('[Push] Permission result:', permission);
  if (permission !== 'granted') {
    console.warn('[Push] Notification permission not granted:', permission);
    return null;
  }

  console.log('[Push] Registering service worker...');
  const registration = await ensureServiceWorkerRegistration();
  if (!registration) {
    console.warn('[Push] Service worker registration not available');
    return null;
  }
  console.log('[Push] Service worker registered:', registration.scope);

  const messaging = getMessaging(firebaseApp);
  let token: string | null = null;

  try {
    console.log('[Push] Getting FCM token...');
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    console.log('[Push] FCM token obtained:', token ? `${token.slice(0, 20)}...` : 'null');
  } catch (error) {
    console.error('[Push] Failed to get FCM token:', error);
    return null;
  }

  if (token) {
    console.log('[Push] Registering token in database...');
    await registerPushToken(token);
  }

  return token || null;
}

export async function subscribeToForegroundMessages(
  handler: (payload: MessagePayload) => void
): Promise<(() => void) | null> {
  const supported = await isMessagingSupported();
  if (!supported || !firebaseApp) {
    return null;
  }

  const messaging = getMessaging(firebaseApp);
  return onMessage(messaging, handler);
}
